import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Injectable, Inject } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import { BQXConfiguration } from 'bqx-ui-common';
import * as i1$1 from 'ngx-logger';
import * as i3 from 'rxjs';
import { throwError } from 'rxjs';
import { retry, catchError, finalize } from 'rxjs/operators';
import stringify from 'fast-stringify';

const authClientCredentials = new InjectionToken('authClientCredentials');
class BqxLoggingModule {
    static forRoot(authToken) {
        return {
            ngModule: BqxLoggingModule,
            providers: [
                { provide: authClientCredentials, useValue: authToken }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxLoggingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: BqxLoggingModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxLoggingModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxLoggingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: []
                }]
        }] });

var LogLevel;
(function (LogLevel) {
    LogLevel["Trace"] = "Trace";
    LogLevel["Debug"] = "Debug";
    LogLevel["Info"] = "Info";
    LogLevel["Warn"] = "Warn";
    LogLevel["Error"] = "Error";
    LogLevel["Fatal"] = "Fatal";
    LogLevel["Off"] = "Off";
})(LogLevel || (LogLevel = {}));

class LogMessage {
    constructor(Level, Message, Source, Sublevel, Stack, Detail) {
        this.Level = Level;
        this.Message = Message;
        this.Source = Source;
        this.Sublevel = Sublevel;
        this.Stack = Stack;
        this.Detail = Detail;
    }
}

class LoggerAbstractService {
}

class LoggerService {
    constructor(http, logger, authToken) {
        this.http = http;
        this.logger = logger;
        this.authToken = authToken;
        this.authToken.subscribe(tokenValue => {
            this._accessToken = tokenValue;
        });
    }
    postHttp(logMessage, httpHeaders) {
        return this.http.post(BQXConfiguration.data.serverLogUrl, logMessage, { headers: httpHeaders, responseType: 'json' });
    }
    indexOfLevel(level) {
        return Object.keys(LogLevel).indexOf(level);
    }
    postLogData(logLevel, message, messageDetail = null, stackTrace = '') {
        //ignore messages logged at a level less serious than the currently configured level
        const configuredLevelNr = this.indexOfLevel(BQXConfiguration.data.level);
        const loggedLevelNr = this.indexOfLevel(logLevel);
        if (loggedLevelNr >= configuredLevelNr) {
            const logMessage = new LogMessage(logLevel, message, BQXConfiguration.data.appInsights.SourceBusinessApplication, BQXConfiguration.data.applicationName, stackTrace, messageDetail);
            const httpHeaders = new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this._accessToken
            });
            this.postHttp(JSON.stringify(logMessage), httpHeaders).subscribe();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoggerService, deps: [{ token: i1.HttpClient }, { token: i1$1.NGXLogger }, { token: authClientCredentials }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoggerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: LoggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: i1$1.NGXLogger }, { type: i3.BehaviorSubject, decorators: [{
                    type: Inject,
                    args: [authClientCredentials]
                }] }] });

class HttpErrorInterceptor {
    constructor(logger) {
        this.logger = logger;
    }
    intercept(request, next) {
        const started = Date.now();
        return next.handle(request)
            .pipe(retry(1), catchError((error) => {
            let errorMessage = '';
            error.error instanceof ErrorEvent
                ? errorMessage = `Error: ${error.error.message}`
                : errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
            this.logger.error(errorMessage);
            if (error.error.base != undefined) {
                this.logger.error(error.error.base[0]);
            }
            return throwError(error);
        }), finalize(() => {
            const elapsed = Date.now() - started;
            this.logger.debug('HTTP Call: ' + request.method + '- Url: ' + request.urlWithParams);
            this.logger.debug('Total response Time: ' + elapsed + 'ms');
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: HttpErrorInterceptor, deps: [{ token: i1$1.NGXLogger }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: HttpErrorInterceptor }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: HttpErrorInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1$1.NGXLogger }] });

class ReportingErrorHandler {
    constructor(logger, loggerService) {
        this.logger = logger;
        this.loggerService = loggerService;
    }
    handleError(error) {
        const errorDtl = {};
        const errorMsg = error.message;
        errorDtl.name = error.name;
        if (error instanceof Error) {
            errorDtl.stack = error?.stack;
        }
        else {
            errorDtl.error = JSON.parse(stringify(error.error));
            errorDtl.headers = error.headers;
            errorDtl.status = error.status;
            errorDtl.statusText = error.statusText;
            errorDtl.url = error.url;
        }
        this.logIt(errorMsg, errorDtl);
    }
    logIt(errorMsg, errorDtl) {
        this.loggerService.postLogData(LogLevel.Error, errorMsg, errorDtl);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ReportingErrorHandler, deps: [{ token: i1$1.NGXLogger }, { token: LoggerAbstractService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ReportingErrorHandler, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: ReportingErrorHandler, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1$1.NGXLogger }, { type: LoggerAbstractService }] });

/*
 * Public API Surface of bqx-logging
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BqxLoggingModule, HttpErrorInterceptor, LogLevel, LogMessage, LoggerAbstractService, LoggerService, ReportingErrorHandler, authClientCredentials };
